import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ 
    "depositCheckbox", "depositAmountField",
    "hiddenStateTextInput", "stateTextInput", "stateList", "stateListScroll", "stateOption",
    "teachSafeFields", "teachSafeCheckbox", "testResult", "testScenario",
    "zubieFields", "zubieCheckbox", "zubieTestResult",
    "improvFields", "improvCheckbox",
    "lessonLengthInput", "lessonLengthError"
  ]

  connect() {
    if (this.stateComboBoxIsActive()) {
      this.onDocClick = this.handleDocumentClick.bind(this);
      this.onDocKeydown = this.handleDocumentKeydown.bind(this);
      document.addEventListener("click", this.onDocClick);
      document.addEventListener("keydown", this.onDocKeydown);
    }

    // Only run if elements exist (to avoid errors if only part of the settings page is rendered)
    // Each controller instance only sees targets within its own scope
    if (this.hasTeachSafeCheckboxTarget && this.hasTeachSafeFieldsTarget) {
      this.toggleTeachSafe()
    }
    if (this.hasZubieCheckboxTarget && this.hasZubieFieldsTarget) {
      this.toggleZubie()
    }
    if (this.hasImprovCheckboxTarget && this.hasImprovFieldsTarget) {
      this.toggleImprov()
    }
  }

  disconnect() {
    if (this.onDocClick) {
      document.removeEventListener("click", this.onDocClick);
    }
    if (this.onDocKeydown) {
      document.removeEventListener("keydown", this.onDocKeydown);
    }
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeStateList();
    }
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape" && this.stateListIsOpen()) {
      event.preventDefault();
      this.closeStateList();
    }
  }

  stateComboBoxIsActive() {
    return (
      this.hasHiddenStateTextInputTarget &&
      this.hasStateTextInputTarget &&
      this.hasStateListTarget
    );
  }

  stateListIsOpen() {
    return !this.stateListTarget.classList.contains("u-hidden");
  }

  openStateList() {
    this.stateListTarget.classList.remove("u-hidden");
    this.stateTextInputTarget.setAttribute("aria-expanded", "true");
    this.filterStates();
    this.resetStateListScroll();
  }

  closeStateList() {
    this.resetStateListScroll();
    this.stateListTarget.classList.add("u-hidden");
    this.stateTextInputTarget.setAttribute("aria-expanded", "false");
    const code = (this.hiddenStateTextInputTarget.value || "").trim().toUpperCase();
    this.stateTextInputTarget.value = this.displayNameForStateCode(code);
  }

  resetStateListScroll() {
    if (!this.hasStateListScrollTarget) return;
    this.stateListScrollTarget.scrollTop = 0;
    requestAnimationFrame(() => {
      this.stateListScrollTarget.scrollTop = 0;
    });
  }

  displayNameForStateCode(code) {
    if (!code) return "";
    const opt = this.stateOptionTargets.find(
      (el) => (el.dataset.code || "").toUpperCase() === code
    );
    return opt ? (opt.dataset.name || "").trim() : "";
  }

  stateInputFocus() {
    this.openStateList();
  }

  stateInput() {
    if (!this.stateListIsOpen()) {
      this.openStateList();
    } else {
      this.filterStates();
    }
  }

  filterStates() {
    const q = this.stateTextInputTarget.value.trim().toLowerCase();
    this.stateOptionTargets.forEach((el) => {
      const haystack = (el.textContent || "").trim().toLowerCase();
      const match = !q || haystack.includes(q);
      el.classList.toggle("u-hidden", !match);
    });
  }

  pickState(event) {
    event.preventDefault();
    const code = (event.currentTarget.dataset.code || "").toUpperCase();
    this.hiddenStateTextInputTarget.value = code;
    this.closeStateList();
  }

  stateInputReconcileOnBlur() {
    const v = this.stateTextInputTarget.value.trim();
    if (!v) {
      this.hiddenStateTextInputTarget.value = "";
      return;
    }
    const normalized = v.toLowerCase();
    const opt = this.stateOptionTargets.find((el) => {
      const name = (el.dataset.name || "").toLowerCase();
      const code = (el.dataset.code || "").toLowerCase();
      const label = `${el.dataset.name} (${el.dataset.code})`.toLowerCase();
      return (
        name === normalized ||
        code === normalized ||
        label === normalized
      );
    });
    if (opt) {
      this.hiddenStateTextInputTarget.value = (opt.dataset.code || "").toUpperCase();
      this.stateTextInputTarget.value = (opt.dataset.name || "").trim();
      return;
    }
    const code = (this.hiddenStateTextInputTarget.value || "").trim().toUpperCase();
    this.stateTextInputTarget.value = this.displayNameForStateCode(code);
  }

  validateLessonLength() {
    const value = parseInt(this.lessonLengthInputTarget.value, 10)
    const isInvalid = isNaN(value) || value < 10
    this.lessonLengthErrorTarget.style.display = isInvalid ? 'block' : 'none'
    this.lessonLengthInputTarget.classList.toggle('Input--error', isInvalid)
  }

  toggleDepositAmount() {
    const checkbox = this.depositCheckboxTarget;
    const amountField = this.depositAmountFieldTarget;
    
    if (checkbox.checked) {
      amountField.style.display = 'block';
    } else {
      amountField.style.display = 'none';
    }
  }

  toggleTeachSafe() {
    if (this.teachSafeCheckboxTarget.checked) {
      this.teachSafeFieldsTarget.style.display = "block"
    } else {
      this.teachSafeFieldsTarget.style.display = "none"
    }
  }

  toggleZubie() {
    if (this.zubieCheckboxTarget.checked) {
      this.zubieFieldsTarget.style.display = "block"
    } else {
      this.zubieFieldsTarget.style.display = "none"
    }
  }

  toggleImprov() {
    if (this.improvCheckboxTarget.checked) {
      this.improvFieldsTarget.style.display = "block"
    } else {
      this.improvFieldsTarget.style.display = "none"
    }
  }

  testConnection(event) {
    event.preventDefault()
    const resultTarget = this.testResultTarget
    resultTarget.textContent = "Testing..."
    resultTarget.className = "ml2 u-textSmall u-textMuted"

    // Note: Since the API key might be changed in the input but not saved, 
    // this test relies on the *saved* configuration in the backend. 
    // We could improve this by sending the form values, but for V1 using saved values is safer.
    
    fetch('/schools/setup/teach_safe/test_connection', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        resultTarget.textContent = data.message
        resultTarget.className = "ml2 u-textSmall u-textGreen"
      } else {
        resultTarget.textContent = data.message
        resultTarget.className = "ml2 u-textSmall u-textRed"
      }
    })
    .catch(error => {
      resultTarget.textContent = "Error testing connection."
      resultTarget.className = "ml2 u-textSmall u-textRed"
    })
  }

  testRegisterStudent(event) {
    event.preventDefault()
    const resultTarget = this.testResultTarget
    const scenario = this.testScenarioTarget ? this.testScenarioTarget.value : 'blank_fields'
    resultTarget.textContent = `Testing registration (Scenario: ${scenario})...`
    resultTarget.className = "ml2 u-textSmall u-textMuted"
    
    fetch('/schools/setup/teach_safe/test_registration', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ scenario: scenario })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        resultTarget.textContent = `${data.message} (Scenario: ${data.scenario})`
        resultTarget.className = "ml2 u-textSmall u-textGreen"
      } else {
        resultTarget.textContent = `${data.message} (Scenario: ${data.scenario})`
        resultTarget.className = "ml2 u-textSmall u-textRed"
      }
    })
    .catch(error => {
      resultTarget.textContent = "Error testing registration."
      resultTarget.className = "ml2 u-textSmall u-textRed"
    })
  }

  testZubieWebhook(event) {
    event.preventDefault()
    const resultTarget = this.zubieTestResultTarget
    const eventType = event.currentTarget.dataset.eventType
    resultTarget.textContent = `Testing ${eventType}...`
    resultTarget.className = "ml2 u-textSmall u-textMuted"
    
    fetch('/schools/setup/zubie/test_webhook', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ event_type: eventType })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        resultTarget.textContent = data.message
        resultTarget.className = "ml2 u-textSmall u-textGreen"
      } else {
        resultTarget.textContent = data.message
        resultTarget.className = "ml2 u-textSmall u-textRed"
      }
    })
    .catch(error => {
      resultTarget.textContent = "Error testing webhook."
      resultTarget.className = "ml2 u-textSmall u-textRed"
    })
  }

}
